import { HeaderNavLink } from 'components/molecules/HeaderNav/HeaderNav';
import { HeaderTitleType } from 'components/organisms/Header/Header';
import { routes } from 'defs';
import { headerNavRouteIds, NavigationRouteItem, offCanvasRouteIds } from 'defs/nav';
import { getParentsOfRoute, getRouteById, RouteType } from 'defs/routes';
import { format } from 'helper/dateTime';
import i18n from 'helper/i18n';
import { ScrollElementIdEnumType } from 'types/ScrollElementId';
import { RootStateType } from '.';
import {
	getCalendarToday,
	getDayByDayNumber,
	getTodayDayNumberFromCalendar,
	ProgramDayMinType,
	ProgramDayType,
} from '../programDays';

export const getCurrentViewID = (state: RootStateType): string | null => {
	return state.view.currentViewID;
};

export const getHeaderTitle = (state: RootStateType, dayNumber?: number): HeaderTitleType => {
	const currentViewID = getCurrentViewID(state);

	// Return emmpty Header Title if not loaed yet
	if (!currentViewID) {
		return {
			text: '',
			icon: '',
		};
	}

	// Return a customized Header Title for /today and /today/:id views
	if (currentViewID === routes.today.indexRoute.id || currentViewID === routes.today.day.id) {
		const todayDayNumber = getTodayDayNumberFromCalendar(state);

		// Return early if today is not known - or if one of those values are not available
		if (typeof todayDayNumber !== 'number') {
			return {
				text: '',
				icon: '',
			};
		}

		const selectedDayNumber = dayNumber || todayDayNumber;
		const selectedDay: ProgramDayType | null = getDayByDayNumber(state, selectedDayNumber);

		if (selectedDay !== null) {
			if (selectedDay.dayNumber === todayDayNumber) {
				// Today
				return {
					text: routes.today.indexRoute.navigationTitle,
					icon: routes.today.indexRoute.icon,
					dayOfMonth: selectedDay.date.dayOfMonth,
				};
			} else if (selectedDay.dayNumber === todayDayNumber - 1) {
				// Yesterday
				return {
					text: i18n('react.impulsCoach.date.labels.yesterday'),
					icon: '',
				};
			} else if (selectedDay.dayNumber === todayDayNumber + 1) {
				// Tomorrow
				return {
					text: i18n('react.impulsCoach.date.labels.tomorrow'),
					icon: '',
				};
			} else if (selectedDay) {
				// All the other days
				return {
					text: format(selectedDay.date.date),
					icon: '',
				};
			}
		} else {
			return {
				text: '',
				icon: '',
			};
		}
	}

	// Return default Header Title for all the other routes
	const route = getRouteById(currentViewID) || ({} as RouteType);

	if (Object.keys(route).length < 1) {
		return {
			text: '',
			icon: '',
		};
	}

	return {
		text: route.navigationTitle,
		icon: route.icon,
	} as any;
};

export const getHeaderNavLinks = (state: RootStateType): Array<HeaderNavLink> => {
	const currentViewID = getCurrentViewID(state);
	const todayDayNumber = ((calendar: ProgramDayMinType | undefined): number => {
		return calendar && calendar.dayNumber ? calendar.dayNumber : -1;
	})(getCalendarToday(state));
	const parentRoutes = getParentsOfRoute(currentViewID);

	return headerNavRouteIds.map((routeItem: NavigationRouteItem) => {
		const route = routes[routeItem.id][routeItem.route || 'indexRoute'];
		const parentRoute = routes[routeItem.id].indexRoute;

		return {
			id: route.id,
			text: route.navigationTitle,
			icon: route.icon,
			/**
			 * Replace /today/:day with todayDayNumber
			 * @see PULSWM-2299
			 * @see PULSWM-3039
			 */
			href: route.href.replace('/:day', todayDayNumber < 1 ? '' : `/${todayDayNumber.toString()}`),
			selected: route.id === currentViewID || parentRoutes.includes(parentRoute.id),
			additions: routeItem.additions || [],
		};
	});
};

export const getOffCanvasNavLinks = (state: RootStateType): Array<HeaderNavLink> => {
	const currentViewID = getCurrentViewID(state);
	const todayDayNumber = ((calendar: ProgramDayMinType | undefined): number => {
		return calendar && calendar.dayNumber ? calendar.dayNumber : 1;
	})(getCalendarToday(state));
	const parentRoutes = getParentsOfRoute(currentViewID);

	return offCanvasRouteIds.map((routeItem: NavigationRouteItem) => {
		const route = routes[routeItem.id][routeItem.route || 'indexRoute'];
		const parentRoute = routes[routeItem.id].indexRoute;

		return {
			id: route.id,
			text: route.navigationTitle,
			icon: route.icon,
			href: route.href.replace(':day', todayDayNumber.toString()),
			selected: route.id === currentViewID || parentRoutes.includes(parentRoute.id),
			additions: routeItem.additions || [],
		};
	});
};

export const getIsOffCanvasOpen = (state: RootStateType): boolean => {
	return state.view.isOffCanvasOpen;
};

export const getIsLoadingView = (state: RootStateType, view: string): boolean => {
	return (state.view[view] || {}).isLoading || false;
};

export const getHasViewError = (state: RootStateType, view: string): boolean => {
	return (state.view[view] || {}).hasError || false;
};

export const getScrollPosition = (
	state: RootStateType,
	scrollElementId?: ScrollElementIdEnumType
): number | undefined => {
	if (!scrollElementId) {
		return state.view.lastScrollPosition as any;
	}

	return state?.view?.lastScrollPositionById[scrollElementId] as any;
};
